import { computed } from 'vue'
import { useFeatureFlagsStore } from '@/stores/featureFlags'
import { useTenantsStore } from '@/stores/tenants'
import { useSelfServeGate } from '@/composables/useSelfServeGate'
import { TenantPlan, isCreditPlan } from '@/models/Tenant'

/**
 * Which plan cards each picker offers — the single source of truth for the
 * setup, resubscribe and plan-switch surfaces (MYAC-837).
 *
 * Two layers, deliberately separated:
 *  - each surface states its INTENT (what it would like to offer, given who the
 *    tenant is and where they are in the funnel);
 *  - `applyPlanCaps` then masks that intent with what the platform still sells
 *    (`enable_legacy_plans`).
 *
 * Caps are always the last word, and every function returns a COMPLETE record,
 * so a key a surface forgot can only ever hide a card, never show one. That
 * ordering is the fix for the prod regression where resubscribe rendered the
 * retired "QMS checker" and "QMS single check" cards: it named only the credit
 * keys, and `PricingPlans.vue` filled the absent ones from permissive defaults
 * (`free: true, evaluator: true`). Both halves are now deny-by-default.
 */
export type PlanKey =
  | 'freeTry'
  | 'free'
  | 'earlyAccess'
  | 'evaluator'
  | 'student'
  | 'veille'
  | 'qms'
  | 'td'
  | 'qmsTd'

export type VisiblePlans = Record<PlanKey, boolean>

export const NO_PLANS: VisiblePlans = {
  freeTry: false,
  free: false,
  earlyAccess: false,
  evaluator: false,
  student: false,
  veille: false,
  qms: false,
  td: false,
  qmsTd: false,
}

/** The retired lineup — every one of these is behind `enable_legacy_plans`. */
const LEGACY_KEYS = ['freeTry', 'free', 'earlyAccess', 'evaluator', 'student'] as const

/** Dev escape hatch that unlocks the early-access lineup on the pickers. */
export const EARLY_ACCESS_STORAGE_KEY = 'setup.enableEarlyAccess'

/** What the platform currently sells, straight off the public feature flags. */
export interface PlanCaps {
  legacyEnabled: boolean
}

export function applyPlanCaps(intent: Partial<VisiblePlans>, caps: PlanCaps): VisiblePlans {
  const plans: VisiblePlans = { ...NO_PLANS, ...intent }
  if (!caps.legacyEnabled) for (const key of LEGACY_KEYS) plans[key] = false
  return plans
}

export interface SetupVisiblePlansInput {
  gated: boolean
  enableEarlyAccess: boolean
  caps: PlanCaps
}

/**
 * Tenant CREATION. Gated: self-serve is Veille-only, so offer just that card —
 * `PricingPlans`' Contact-sales card carries everything else. (Veille ≠ Free:
 * Free is not self-servable.)
 */
export function setupVisiblePlans(i: SetupVisiblePlansInput): VisiblePlans {
  if (i.gated) return applyPlanCaps({ veille: true }, i.caps)
  if (i.caps.legacyEnabled) {
    return applyPlanCaps(
      {
        earlyAccess: i.enableEarlyAccess,
        evaluator: !i.enableEarlyAccess,
        free: !i.enableEarlyAccess,
      },
      i.caps,
    )
  }
  return applyPlanCaps({ veille: true, qms: true, td: true, qmsTd: true }, i.caps)
}

export interface ResubscribeVisiblePlansInput {
  gated: boolean
  currentPlan: TenantPlan | string | null
  enableEarlyAccess: boolean
  caps: PlanCaps
}

/**
 * Resubscribe after a lapsed subscription — an existing-tenant plan change, so
 * never self-serve while gated.
 *
 * A tenant whose legacy plan has since been retired (`enable_legacy_plans` off)
 * gets no card at all rather than a silent re-pricing onto the credit lineup:
 * that move is a sales conversation, and Contact sales is always rendered.
 */
export function resubscribeVisiblePlans(i: ResubscribeVisiblePlansInput): VisiblePlans {
  if (i.gated) return { ...NO_PLANS }
  if (isCreditPlan(i.currentPlan)) {
    return applyPlanCaps({ veille: true, qms: true, td: true, qmsTd: true }, i.caps)
  }
  return applyPlanCaps(
    {
      earlyAccess: i.enableEarlyAccess,
      evaluator: !i.enableEarlyAccess,
      free: i.currentPlan === TenantPlan.FREE_TRY,
    },
    i.caps,
  )
}

export interface PlanSwitchVisiblePlansInput {
  currentPlan: TenantPlan | string | null
  caps: PlanCaps
}

/**
 * The in-app upgrade dialog. Entry is already gated at `PlanSwitchButton`, so
 * this only decides the lineup: current plan (for a tier change) plus upgrades,
 * along the hierarchy Veille < QMS = TD < QMS+TD.
 */
export function planSwitchVisiblePlans(i: PlanSwitchVisiblePlansInput): VisiblePlans {
  const current = i.currentPlan
  if (isCreditPlan(current)) {
    const isVeille = current === TenantPlan.VEILLE
    const isQms = current === TenantPlan.QMS
    const isTd = current === TenantPlan.TD
    const isQmsTd = current === TenantPlan.QMS_TD
    return applyPlanCaps(
      {
        qms: isVeille || isQms,
        td: isVeille || isTd,
        qmsTd: isVeille || isQms || isTd || isQmsTd,
      },
      i.caps,
    )
  }
  return applyPlanCaps(
    {
      free: current !== TenantPlan.FREE && current !== TenantPlan.EVALUATOR,
      evaluator: current !== TenantPlan.EVALUATOR,
    },
    i.caps,
  )
}

/**
 * Store-backed wiring for the three pickers: flags come from `featureFlags`,
 * the plan catalogue from `billing.pricingPlans`, the current tenant from
 * `tenants`. Views bind a computed and pass nothing by hand — the whole point
 * is that no surface re-derives this policy from raw flags again.
 */
export function usePlanVisibility() {
  const featureFlagsStore = useFeatureFlagsStore()
  const tenantsStore = useTenantsStore()
  const { selfServeEnabled } = useSelfServeGate()

  const caps = computed<PlanCaps>(() => ({
    legacyEnabled: featureFlagsStore.isLegacyPlansEnabled,
  }))

  const legacyPlansAllowed = computed(() => caps.value.legacyEnabled)
  const currentPlan = computed(() => tenantsStore.currentTenant?.plan ?? null)
  const enableEarlyAccess = computed(
    () => localStorage.getItem(EARLY_ACCESS_STORAGE_KEY) === 'true',
  )

  const setupPlans = computed(() =>
    setupVisiblePlans({
      gated: !selfServeEnabled.value,
      enableEarlyAccess: enableEarlyAccess.value,
      caps: caps.value,
    }),
  )

  const resubscribePlans = computed(() =>
    resubscribeVisiblePlans({
      gated: !selfServeEnabled.value,
      currentPlan: currentPlan.value,
      enableEarlyAccess: enableEarlyAccess.value,
      caps: caps.value,
    }),
  )

  const planSwitchPlans = computed(() =>
    planSwitchVisiblePlans({ currentPlan: currentPlan.value, caps: caps.value }),
  )

  return { caps, legacyPlansAllowed, setupPlans, resubscribePlans, planSwitchPlans }
}
